import { useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import {
  AddCircleOutline as AttachFileIcon,
  Mic as MicIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material'
import type { PendingWebsiteTarget, WebsiteAnchorRect, WebsiteApplyMode } from './types'
import { TargetChipBar } from './TargetChipBar'

export interface ContextualWebsiteComposerProps {
  value: string
  targets: PendingWebsiteTarget[]
  applyMode: WebsiteApplyMode
  anchorRect?: WebsiteAnchorRect | null
  /** Bounds use the same iframe-viewport coordinate space as anchorRect. */
  containerSize?: { width: number; height: number }
  /** Measured composer size when available; defaults match the native compact surface. */
  composerSize?: { width: number; height: number }
  narrow?: boolean
  disabled?: boolean
  runningLabel?: string
  pendingFileNames?: string[]
  voiceStatus?: 'idle' | 'connecting' | 'recording' | 'stopping'
  voiceTranscript?: string
  onChange: (value: string) => void
  onRemoveTarget: (targetId: string) => void
  onApplyModeChange: (mode: WebsiteApplyMode) => void
  onAttach?: (files: File[]) => void
  onVoice?: () => void
  onVoiceAccept?: () => void
  onVoiceCancel?: () => void
  onSend: () => void
  onClose: () => void
}

export function placeContextualComposer(anchor: WebsiteAnchorRect | null | undefined, container: { width: number; height: number }, size = { width: 360, height: 270 }) {
  const margin = 8, gap = 12
  const width = Math.max(0, Math.min(size.width, container.width - margin * 2))
  const height = Math.max(0, Math.min(size.height, container.height - margin * 2))
  if (!anchor) return { left: margin, top: margin, width }
  const candidates = [
    { left: anchor.x + anchor.width + gap, top: anchor.y },
    { left: anchor.x - width - gap, top: anchor.y },
    { left: anchor.x, top: anchor.y + anchor.height + gap },
    { left: anchor.x, top: anchor.y - height - gap },
  ]
  const fits = ({ left, top }: { left: number; top: number }) => left >= margin && top >= margin && left + width <= container.width - margin && top + height <= container.height - margin
  const chosen = candidates.find(fits) ?? candidates[0]
  return {
    left: Math.max(margin, Math.min(chosen.left, container.width - width - margin)),
    top: Math.max(margin, Math.min(chosen.top, container.height - height - margin)),
    width,
  }
}

export function ContextualWebsiteComposer({
  value, targets, applyMode, anchorRect, containerSize = { width: 760, height: 600 }, composerSize = { width: 360, height: 270 }, narrow = false, disabled = false, runningLabel, pendingFileNames = [], voiceStatus = 'idle', voiceTranscript = '',
  onChange, onRemoveTarget, onApplyModeChange, onAttach, onVoice, onVoiceAccept, onVoiceCancel, onSend, onClose,
}: ContextualWebsiteComposerProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const canSend = value.trim().length > 0 && !disabled
  const anchorSx = narrow ? { left: 1, right: 1, bottom: 1 } : placeContextualComposer(anchorRect, containerSize, composerSize)
  return (
    <Box sx={{ position: 'absolute', zIndex: 4, ...anchorSx }}>
      <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 3, boxShadow: 4, p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ flex: 1, fontSize: '0.78rem', fontWeight: 700 }}>Edit selected element{targets.length === 1 ? '' : 's'}</Typography>
          <Tooltip title="Close composer"><IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary', p: 0.5 }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        </Box>
        <TargetChipBar targets={targets} onRemove={onRemoveTarget} disabled={disabled} />
        {!!pendingFileNames.length && <Typography sx={{ mt: 0.75, fontSize: '0.7rem', color: 'text.secondary' }}>{pendingFileNames.join(', ')}</Typography>}
        {voiceStatus === 'idle' ? (
          <TextField
            fullWidth multiline maxRows={5} value={value} disabled={disabled}
            onChange={event => onChange(event.target.value)}
            onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && canSend) { event.preventDefault(); onSend() } }}
            placeholder="Tell Ros what to change…" variant="standard" InputProps={{ disableUnderline: true }}
            sx={{ mt: 1, '& .MuiInputBase-root': { fontSize: '0.9rem', lineHeight: 1.6, py: 0.5 } }}
          />
        ) : (
          <Typography sx={{ mt: 1, minHeight: '2.2em', fontSize: '0.9rem', lineHeight: 1.6, color: voiceTranscript ? 'text.primary' : 'text.secondary' }}>
            {voiceTranscript || (voiceStatus === 'connecting' ? 'Connecting…' : 'Listening…')}
          </Typography>
        )}
        {runningLabel && <Typography sx={{ fontSize: '0.7rem', color: 'primary.main', mb: 0.5 }}>{runningLabel}</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <input ref={fileInput} hidden type="file" multiple onChange={e => { if (e.target.files) onAttach?.(Array.from(e.target.files)); e.target.value = '' }} />
          <Tooltip title="Attach media"><span><IconButton size="small" disabled={!onAttach || disabled} onClick={() => fileInput.current?.click()} sx={{ color: 'text.secondary', p: 0.5 }}><AttachFileIcon sx={{ fontSize: 20 }} /></IconButton></span></Tooltip>
          {voiceStatus === 'idle' ? (
            <Tooltip title="Voice input"><span><IconButton size="small" disabled={!onVoice || disabled} onClick={onVoice} sx={{ color: 'text.secondary', p: 0.5 }}><MicIcon sx={{ fontSize: 20 }} /></IconButton></span></Tooltip>
          ) : (
            <>
              <Tooltip title="Discard recording"><span><IconButton size="small" disabled={!onVoiceCancel} onClick={onVoiceCancel} sx={{ color: 'text.secondary', p: 0.5 }}><CloseIcon sx={{ fontSize: 19 }} /></IconButton></span></Tooltip>
              <Tooltip title="Use transcript"><span><IconButton size="small" disabled={voiceStatus !== 'recording' || !onVoiceAccept} onClick={onVoiceAccept} sx={{ color: 'primary.main', p: 0.5 }}><CheckIcon sx={{ fontSize: 20 }} /></IconButton></span></Tooltip>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <ButtonGroup size="small" variant="text" aria-label="Apply behavior">
            <Button onClick={() => onApplyModeChange('review')} disabled={disabled} color={applyMode === 'review' ? 'primary' : 'inherit'} sx={{ fontSize: '0.68rem', fontWeight: applyMode === 'review' ? 700 : 500 }}>Review</Button>
            <Button onClick={() => onApplyModeChange('auto')} disabled={disabled} color={applyMode === 'auto' ? 'primary' : 'inherit'} sx={{ fontSize: '0.68rem', fontWeight: applyMode === 'auto' ? 700 : 500 }}>Auto</Button>
          </ButtonGroup>
          <Tooltip title="Send (Enter)"><span><IconButton disabled={!canSend} onClick={onSend} sx={{ color: canSend ? 'primary.main' : 'action.disabled', p: 0.5 }}><SendIcon sx={{ fontSize: 22 }} /></IconButton></span></Tooltip>
        </Box>
      </Box>
    </Box>
  )
}
